import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTenants } from '@/services/tenant-service';
import { uploadPbixReport } from '@/services/workspace-service';
import { WorkspaceUploadRequest } from '@/types/workspace';

export function UploadReportScreen() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const {
    data: tenants,
  } = useQuery({
    queryKey: ["getTenants"],
    queryFn: getTenants,
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };
  const resetForm = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (formRef.current) {
      formRef.current.reset();
    }
    setSelectedFile(null);
    setSelectedTenantId(null); 
  };
  const uploadMutation = useMutation({
    mutationFn: uploadPbixReport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast({
        title: 'Success',
        description: 'Report uploaded successfully',
      });
      resetForm()
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to upload report',
        variant: 'destructive',
      });
    },
  });

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFile && selectedTenantId) {
      const selectedTenant = tenants?.find(t => t.tenantId.toString() === selectedTenantId);
      if (!selectedTenant) {
        toast({
          title: 'Error',
          description: 'Invalid tenant selected',
          variant: 'destructive',
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target && typeof event.target.result === 'string') {
          const request: WorkspaceUploadRequest = {
            workspaceId: selectedTenant.workspaceId,
            clientId: selectedTenant.clientId,
            fileName: selectedFile.name,
            dataFile: event.target.result
          };
          uploadMutation.mutate(request)
        }
      };
      reader.readAsDataURL(selectedFile);
    } else {
      toast({
        title: 'Error',
        description: 'Please select a file and a tenant',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto mt-8">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">Upload Report</CardTitle>
      </CardHeader>
      <CardContent>
      <form ref={formRef} onSubmit={handleUpload} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="tenant">Select Tenant</Label>
            <Select 
              onValueChange={setSelectedTenantId} 
              value={selectedTenantId || undefined}
              key={selectedTenantId}
            >
              <SelectTrigger id="tenant">
                <SelectValue placeholder="Select a tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants?.map((tenant) => (
                  <SelectItem key={tenant.tenantId} value={tenant.tenantId.toString()}>
                    {tenant.profileName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="file">Select PBIX File</Label>
            <Input ref={fileInputRef} id="file" type="file" onChange={handleFileChange} accept=".pbix" />
          </div>
          <Button type="submit" className="w-full" disabled={!selectedFile || !selectedTenantId || uploadMutation.isPending}>
            {uploadMutation.isPending ? 'Uploading...' : 'Upload Report'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}